// GovernanceRoleRegistry — managed governance role-definition CRUD UI.
// (Admin-plane RBAC, Task 17.)
//
// Lists all role-defs: built-ins (read-only/locked) + custom (editable).
// Create: name, description, permission picker grouped by subsystem.
// Update (custom only): description + permissions.
// Delete (custom only): irreversible, two-phase confirm.
//
// API surface: /v1/admin/tenants/{tenantId}/governance/role-defs
// Gated by gov:roledef.manage (seeded into audit-admin).
// Built-in roles (security-admin/audit-admin/policy-admin) respond 409 to
// update/delete; this page enforces that UX-side (no buttons shown) AND the
// @devnw/cyber-iam/rbac/RoleRegistryManager component enforces it again
// internally (locked built-in rows, re-validation at confirm time).
//
// OIDCPAM Gate-6 Phase 6a: migrated off MUI onto
// @devnw/cyber-iam/rbac/RoleRegistryManager — the exact parity component
// built from this page. The component owns the built-in/custom CRUD forms,
// the permission picker (rbac/PermissionPicker, extended with
// enableGroupSelectAll), and the delete two-phase confirm; this page still
// owns every API call, the wire-mapping
// (../api/governanceRoleRegistryMapping), the permission catalog, the
// 409/400 friendly-error translation, and tenant gating. The component's
// built-in "Custom roles take effect only once the identity provider issues
// tokens carrying the role name in the admin_role claim" note replaces this
// page's former standalone enforcement Alert.
import React, { useCallback, useEffect, useState } from 'react'
import styled from 'styled-components'
import { asUnified, ut } from '@devnw/theme'
// eslint-import-resolver-node (the resolver wired in .eslintrc.json) does not
// understand package.json "exports" maps, so it cannot follow @devnw's
// wildcard subpath export. The module resolves correctly at build/runtime
// (webpack, Node, ts-jest) via the "exports" field — see
// __tests__/devnw-smoke.test.tsx for the Phase 0 proof.
// eslint-disable-next-line import/no-unresolved
import RoleRegistryManager from '@devnw/cyber-iam/rbac/RoleRegistryManager/RoleRegistryManager'
import type {
  RoleDef,
  RoleDefSubmission,
  PermissionGroup,
} from '@devnw/cyber-iam/iam/types'
import { useAdminGenClient } from '../api/genClient'
import { useActiveTenant } from '../layout/TenantSwitcher'
import {
  toIamRoleDef,
  type WireRoleDef,
} from '../api/governanceRoleRegistryMapping'

// ---------------------------------------------------------------------------
// Permission catalog — hardcoded stable set of all 62 valid permissions,
// grouped by subsystem to drive RoleRegistryManager's permission pickers.
// This mirrors the Go PermCatalog in pam_permissions.go. The library never
// hardcodes a catalog itself — it is a REQUIRED host-supplied prop.
// ---------------------------------------------------------------------------

export const PERMISSION_CATALOG: PermissionGroup[] = [
  {
    group: 'gov',
    label: 'Governance',
    permissions: [
      'gov:discovery.run',
      'gov:discovery.list',
      'gov:drift.report',
      'gov:dualauth.propose',
      'gov:dualauth.approve',
      'gov:dualauth.reject',
      'gov:dualauth.list',
      'gov:roles.list',
      'gov:roles.assign',
      'gov:roledef.manage',
      'gov:campaign.create',
      'gov:campaign.list',
      'gov:campaign.get',
      'gov:campaign.launch',
      'gov:campaign.close',
      'gov:attestation.list',
      'gov:attestation.submit',
    ],
  },
  {
    group: 'audit',
    label: 'Audit',
    permissions: ['audit:query', 'audit:casefile.get'],
  },
  {
    group: 'breakglass',
    label: 'Break-Glass',
    permissions: [
      'breakglass:role.create',
      'breakglass:role.list',
      'breakglass:role.get',
      'breakglass:role.update',
      'breakglass:role.delete',
      'breakglass:activate',
      'breakglass:event.list',
      'breakglass:event.list_unreviewed',
      'breakglass:event.get',
      'breakglass:event.review',
    ],
  },
  {
    group: 'session',
    label: 'Sessions',
    permissions: [
      'session:initiate',
      'session:list',
      'session:get',
      'session:monitor',
      'session:pause',
      'session:resume',
      'session:kill',
      'session:recordings.get',
      'session:control_policy.get',
      'session:control_policy.set',
      'session:control_policy.delete',
    ],
  },
  {
    group: 'device',
    label: 'Device Posture',
    permissions: ['device:posture.upsert', 'device:posture.get'],
  },
  {
    group: 'policy',
    label: 'Policy',
    permissions: [
      'policy:create',
      'policy:list',
      'policy:get',
      'policy:update',
      'policy:delete',
      'policy:versions.list',
      'policy:version.get',
      'policy:rollback',
      'policy:simulate',
    ],
  },
  {
    group: 'accessreq',
    label: 'Access Requests',
    permissions: [
      'accessreq:list',
      'accessreq:create',
      'accessreq:get',
      'accessreq:approve',
      'accessreq:deny',
      'accessreq:cancel',
      'accessreq:extend',
      'accessreq:activate',
      'accessreq:evidence.get',
      'accessreq:evidence_package.get',
    ],
  },
  {
    group: 'resilience',
    label: 'Resilience',
    permissions: ['resilience:maintenance.toggle'],
  },
]

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function errMessage(e: unknown): string {
  return e instanceof Error ? e.message : 'Request failed'
}

/** Map HTTP status codes in error messages to friendly UI text. */
function friendlyApiError(
  e: unknown,
  context: 'create' | 'update' | 'delete'
): string {
  const msg = errMessage(e)
  // The generated ApicClient emits HTTPError with a .status property and a
  // structured message extracted from the JSON body (e.g. "name collides with
  // a built-in role"). Checking msg.includes('409') only fires when the body
  // had no structured message. Read .status directly for reliability.
  const httpStatus =
    e !== null && typeof e === 'object' && 'status' in e
      ? (e as { status: number }).status
      : 0
  // 409 → built-in immutable OR name already in use
  const is409 =
    httpStatus === 409 ||
    msg.includes('409') ||
    msg.toLowerCase().includes('conflict')
  if (is409) {
    if (context === 'create') {
      return 'A role with that name already exists (or that name is reserved for a built-in role).'
    }
    return 'Built-in roles cannot be modified or deleted.'
  }
  // 400 → unknown permission in the body
  const is400 =
    httpStatus === 400 ||
    msg.includes('400') ||
    msg.toLowerCase().includes('bad request') ||
    msg.toLowerCase().includes('unknown permission')
  if (is400) {
    return 'The request contained an unknown permission name. Check the permission catalog.'
  }
  return msg
}

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => ut(asUnified(theme)).space('lg')};
`

const MutedText = styled.p`
  margin: 0;
  color: ${({ theme }) => asUnified(theme).text.secondary};
`

// ---------------------------------------------------------------------------
// Main page component
// ---------------------------------------------------------------------------

export const GovernanceRoleRegistry = (): JSX.Element => {
  const { tenant } = useActiveTenant()
  const client = useAdminGenClient()

  const [roleDefs, setRoleDefs] = useState<RoleDef[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async (): Promise<void> => {
    if (!tenant) return
    setLoading(true)
    setError(null)
    try {
      const data = await client.adminListRoleDefs({ tenantId: tenant })
      // The generated client's fromWire() camelCases the opaque
      // Array<Record<string, unknown>> response — see
      // ../api/governanceRoleRegistryMapping.ts for the real shape.
      setRoleDefs(
        ((data.items ?? []) as unknown as WireRoleDef[]).map(toIamRoleDef)
      )
    } catch (e: unknown) {
      setError(errMessage(e))
    } finally {
      setLoading(false)
    }
  }, [tenant, client])

  useEffect(() => {
    load()
      .then(() => {})
      .catch(() => {})
  }, [load])

  const handleRefresh = useCallback((): void => {
    load()
      .then(() => {})
      .catch(() => {})
  }, [load])

  const handleCreate = async (submission: RoleDefSubmission): Promise<void> => {
    if (!tenant) return
    try {
      await client.adminCreateRoleDef({
        tenantId: tenant,
        name: submission.name,
        description: submission.description,
        permissions: submission.permissions,
      })
    } catch (e: unknown) {
      throw new Error(friendlyApiError(e, 'create'))
    }
  }

  const handleUpdate = async (
    name: string,
    submission: RoleDefSubmission
  ): Promise<void> => {
    if (!tenant) return
    try {
      await client.adminUpdateRoleDef({
        tenantId: tenant,
        name,
        description: submission.description,
        permissions: submission.permissions,
      })
    } catch (e: unknown) {
      throw new Error(friendlyApiError(e, 'update'))
    }
  }

  const handleDelete = async (name: string): Promise<void> => {
    if (!tenant) return
    try {
      await client.adminDeleteRoleDef({ tenantId: tenant, name })
    } catch (e: unknown) {
      throw new Error(friendlyApiError(e, 'delete'))
    }
  }

  if (!tenant) {
    return (
      <PageContainer>
        <MutedText>
          Select a tenant to manage governance role definitions.
        </MutedText>
      </PageContainer>
    )
  }

  return (
    <PageContainer>
      <RoleRegistryManager
        testID="governance-role-registry"
        title="Governance Role Registry"
        description="Manage fine-grained permission-based admin roles. Built-in roles (security-admin, audit-admin, policy-admin) are read-only and anchor separation-of-duties. Custom roles are enforced by this server once the identity provider issues tokens carrying the role name in the admin_role claim."
        roles={roleDefs}
        permissionCatalog={PERMISSION_CATALOG}
        onCreate={handleCreate}
        onUpdate={handleUpdate}
        onDelete={handleDelete}
        onRefresh={handleRefresh}
        loading={loading}
        error={error ?? undefined}
      />
    </PageContainer>
  )
}
